// checkout/utils/updateCheckoutStepUrl.ts

/**
 * A list of accepted checkout step identifiers that are allowed
 * to update the browser URL. These correspond to known steps in the
 * single-page checkout flow.
 */
const acceptedStepIds = ['login', 'accountDetails', 'deliveryAddress', 'paymentMethod'];

/**
 * Updates the browser URL to reflect the current checkout step.
 *
 * This function is typically called when a form is loaded during the
 * checkout process. It extracts the step identifier from the form ID
 * and, if it's a known step, updates the URL using the History API
 * without reloading the page.
 *
 * @param {string} formId - The identifier of the current checkout form.
 */
export function updateCheckoutStepUrl(formId: string) {
	const stepId = getCurrentStepId(formId);

	//console.log(`STEP ID: ${stepId}`);

	if (!stepId || !acceptedStepIds.includes(stepId)) {
		return;
	}

	if (stepId !== 'login') {
		const base = window.serverData?.contextPath || '';
		const newUrl = base + '/checkout/' + stepId;

		if (window.location.pathname !== newUrl) {
			window.history.pushState(null, '', newUrl);
		}
		const fire = () => {
			window.GTM?.createCheckoutEvent?.(
				stepId as 'accountDetails' | 'deliveryAddress' | 'paymentMethod',
			);

			if (window.serverData?.convivaEnabled) {
				window.apptracker?.('trackPageView', { title: stepId });
			}
		};

		if (document.readyState === 'complete') {
			fire();
		} else {
			window.addEventListener('load', fire, { once: true });
		}
	} else {
		if (document.readyState === 'complete') {
			window.GTM?.createCheckoutEvent?.('loginOrRegister');
		} else {
			window.addEventListener(
				'load',
				function () {
					window.GTM?.createCheckoutEvent?.('loginOrRegister');
				},
				{ once: true },
			);
		}
	}
}

/**
 * Extracts the checkout step ID from the given form identifier.
 *
 * If the form ID ends with "Form", it removes that suffix to return
 * just the step name (e.g., "deliveryAddressForm" → "deliveryAddress").
 * If not, it returns the form ID as is.
 *
 * @param {string} formId - The identifier of the current checkout form.
 * @returns {string|null} The current step ID or null if not found.
 */
function getCurrentStepId(formId: string): string | null {
	// Simplify ID to just step name
	if (formId.endsWith('Form')) {
		return formId.replace('Form', '');
	}

	return formId;
}
